import { inject, Injectable } from '@angular/core';
import { BsApiService, SimpleGlobalModalService } from '@brightside-web/desktop/data-access/core-services';
import { catchError, map, take, tap } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, Observable, Subject, throwError } from 'rxjs';
import { Router } from '@angular/router';

export enum DocumentsKeyEnum {
  SAVINGS_ACCOUNT = "savings_account_creation",
  SPENDING_ACCOUNT = "spending_account_creation",
  CASH_ADVANCE = "cash_advance_creation",
  Kashable = "kashable_loan_creation",
  SalaryFinance = "salary_finance_loan_creation",
  TrueConnect = "true_connect_loan_creation",
  CREDIT_LINKING = "credit_linking",
  LOGIN = "login",
  REGISTRATION = "registration",
}

export interface LegalDocsResponsePayloadItems {
  titleCopyKey: string;
  descriptionCopyKey: string;
  downloadUrlCopyKey: string;
  downloadUrl: string;
  documentVersionId: number;
}

export interface LegalDocsCopyKeys {
  titleCopy?: string
  descriptionCopy?: string;
}

export interface LegalDocsResponsePayload {
  legalDocuments: LegalDocsResponsePayloadItems[];
  documentGroupKey: string;
  deferrable: boolean;
  titleCopy?: string
  descriptionCopy?: string;
}

export interface LegalDocsResponse {
  payload?: LegalDocsResponsePayload;
}

interface LegalDocsGetRequired {
  documentsKey: DocumentsKeyEnum;
}

export enum LegalDocsAcceptActions {
  ACCEPTED= 'ACCEPTED',
  DECLINED = 'DECLINED',
  DEFERRED = 'DEFERRED'
}

interface LegalDocsAccept {
  documentVersionIds: number[];
  action: LegalDocsAcceptActions,
  documentGroupKey: string;
}

@Injectable()
export class LegalDocumentsService {
  apiPath = '/legal-docs';
  apiName = 'api-mobile';
  activeDocumentsKey: DocumentsKeyEnum;
  activeRequiredDocuments: LegalDocsResponsePayloadItems[];
  activeDocumentGroupKey: string;

  private readonly _router = inject(Router);
  private readonly _sModalService = inject(SimpleGlobalModalService);

  private _requiredDocuments = new Subject<LegalDocsResponsePayloadItems[]>();
  public readonly requiredDocuments: Observable<LegalDocsResponsePayloadItems[]> = this._requiredDocuments.asObservable();

  private _docCopyKeys = new Subject<LegalDocsCopyKeys>();
  public readonly docCopyKeys: Observable<LegalDocsCopyKeys> = this._docCopyKeys.asObservable();

  private _deferrable = new BehaviorSubject<boolean>(false);
  public readonly deferrable: Observable<boolean> = this._deferrable.asObservable();

  private _processing = new BehaviorSubject<boolean>(false);
  public readonly processing: Observable<boolean> = this._processing.asObservable();

  private _displayGeneralError = new BehaviorSubject<boolean>(false);
  public readonly displayGeneralError: Observable<boolean> = this._displayGeneralError.asObservable();

  private _docsSubmitted = new Subject<boolean>();
  public readonly docsSubmitted: Observable<boolean> = this._docsSubmitted.asObservable();

  private _displayDocsRequiredModal = new BehaviorSubject<boolean>(false);
  public readonly displayDocsRequiredModal: Observable<boolean> = this._displayDocsRequiredModal.asObservable();

  constructor(private _bsAPI: BsApiService) {}

  getDocsByKey(documentsKey: DocumentsKeyEnum): Observable<LegalDocsResponse> {
    return this._bsAPI.get<LegalDocsResponse>(this.apiName, `${this.apiPath}/packet/${documentsKey}/step`, { cache: true });
  }
  getRequiredDocuments(documentsKey: DocumentsKeyEnum, bypassOnError = false) {
    this.activeDocumentsKey = documentsKey;
    this.hideAdditionalDocsModal();
    this._processing.next(false);
    const shouldBypass = bypassOnError;
    this.getDocsByKey(documentsKey)
      .pipe(
        take(1),
        map((resp) => {
          if (resp.payload?.legalDocuments && resp.payload.legalDocuments.length > 0) {
            resp.payload.legalDocuments = this._uppercaseCopyKeys(resp.payload.legalDocuments);
          }
          return resp;
        }),
        catchError((err) => {
          this.showRetryModal(documentsKey, shouldBypass);
          return EMPTY;
        })
      )
      .subscribe((resp) => {
        if (resp.payload) {
          this.activeDocumentsKey = documentsKey;
          this._setActivePayload(resp.payload);
        }
        if (resp.payload?.legalDocuments.length === 0) {
          this._docsSubmitted.next(true);
        }
      });
  }

  acceptRequiredDocuments(action: LegalDocsAcceptActions = LegalDocsAcceptActions.ACCEPTED) {
    this._processing.next(true);

    this._bsAPI
      .post<LegalDocsResponse>(this.apiName, `${this.apiPath}/packet/${this.activeDocumentsKey}/step`, <LegalDocsAccept>{
        action: action,
        documentVersionIds: this._getDocumentIdArray(),
        documentGroupKey: this.activeDocumentGroupKey,
      })
      .pipe(
        take(1),
        tap((resp) => {
          switch (resp.result.code) {
            case 'ADDITIONAL_LEGAL_DOCS_REQUIRED':
              this._processing.next(false);
              this.showAdditionalDocsModal();
              break;
            case 'SERVICE_OK':
              this._processing.next(false);
              if (resp.payload?.legalDocuments.length === 0) {
                this._docsSubmitted.next(true);
              } else if (resp.payload) {
                this._setActivePayload(resp.payload);
              }
              break;
            default:
              throw new Error('Unhandled Response code');
          }
        }),
        catchError((error) => {
          this._processing.next(false);
          this._docsSubmitted.next(false);
          this.showGeneralError();
          return throwError(() => error);
        })
      )
      .subscribe();
  }

  getAcceptedDocuments(): Observable<LegalDocsResponse> {
    return this._bsAPI.get<LegalDocsResponse>(this.apiName, `${this.apiPath}/accepted`);
  }

  showAdditionalDocsModal() {
    this._displayDocsRequiredModal.next(true);
  }
  hideAdditionalDocsModal() {
    this._displayDocsRequiredModal.next(false);
  }
  showGeneralError() {
    this._displayGeneralError.next(true);
  }
  hideGeneralError() {
    this._displayGeneralError.next(false);
  }

  private _getDocumentIdArray(): number[] {
    const documentIdArray: number[] = [];
    this.activeRequiredDocuments.forEach((doc) => {
      documentIdArray.push(doc.documentVersionId);
    });
    return documentIdArray;
  }

  private _uppercaseCopyKeys(legalDocs: LegalDocsResponsePayloadItems[]): LegalDocsResponsePayloadItems[] {
    legalDocs.forEach((doc) => {
      doc.titleCopyKey = doc.titleCopyKey ? doc.titleCopyKey.toUpperCase() : '';
      doc.descriptionCopyKey = doc.descriptionCopyKey ? doc.descriptionCopyKey.toUpperCase() : '';
      doc.downloadUrlCopyKey = doc.downloadUrlCopyKey ? doc.downloadUrlCopyKey.toUpperCase() : '';
    });
    return legalDocs;
  }

  private _setActivePayload(payload: LegalDocsResponsePayload): void {
    this.activeRequiredDocuments = payload.legalDocuments;
    this._requiredDocuments.next(payload.legalDocuments);
    this.activeDocumentGroupKey = payload.documentGroupKey;
    this._docCopyKeys.next({
      titleCopy: payload.titleCopy,
      descriptionCopy: payload.descriptionCopy,
    });
    this._deferrable.next(payload.deferrable);
  }

  private showRetryModal(documentsKey: DocumentsKeyEnum, shouldBypass: boolean) {
    if (shouldBypass) {
      this._sModalService.createGenericErrorModal(
        'legal_docs_error',
        () => {
          this.getRequiredDocuments(documentsKey, shouldBypass);
        },
        'TRY_AGAIN',
        () => {
          this._sModalService.clearModal();
          this._router.navigate(['home']);
        }
      );
    } else {
      this._processing.next(false);
      this._docsSubmitted.next(false);
      this.showGeneralError();
    }
  }
}
